/**
 * Plain-text projection of a BlockNote document. Good enough for search,
 * display names and giving the assistant something to read; it is not a
 * faithful serialisation (use the editor's markdown export for that).
 */

interface InlineLike {
  type?: string
  text?: string
  content?: unknown
}

interface BlockLike {
  type?: string
  props?: Record<string, unknown>
  content?: unknown
  children?: unknown
}

const inlineText = (content: unknown): string => {
  if (typeof content === 'string') return content
  if (!Array.isArray(content)) {
    // table content: { type: 'tableContent', rows: [{ cells: [inline[]] }] }
    const rows = (content as { rows?: { cells?: unknown[] }[] } | undefined)?.rows
    if (Array.isArray(rows)) return rows.map((r) => (r.cells ?? []).map(inlineText).join(' | ')).join('\n')
    return ''
  }
  return content
    .map((item) => {
      const i = item as InlineLike
      if (typeof i.text === 'string') return i.text
      if (i.content) return inlineText(i.content)
      return ''
    })
    .join('')
}

const blockText = (b: BlockLike, depth: number): string[] => {
  const lines: string[] = []
  const indent = '  '.repeat(depth)
  // a diagram keeps its fence, so the text can be read and rewritten as markdown
  if (b.type === 'mermaid') {
    const src = inlineText(b.content).replace(/\n+$/, '')
    lines.push(`${indent}\`\`\`mermaid\n${src}\n${indent}\`\`\``)
    if (Array.isArray(b.children))
      for (const c of b.children) lines.push(...blockText(c as BlockLike, depth + 1))
    return lines
  }
  let line = inlineText(b.content)
  if (b.type === 'checkListItem') line = `${b.props?.checked ? '[x]' : '[ ]'} ${line}`
  if (b.type === 'task') line = `${b.props?.done ? '[x]' : '[ ]'} ${line}`
  if (line.trim()) lines.push(indent + line)
  if (Array.isArray(b.children))
    for (const c of b.children) lines.push(...blockText(c as BlockLike, depth + 1))
  return lines
}

export const blocksToText = (blocks: unknown[]): string =>
  blocks.flatMap((b) => blockText(b as BlockLike, 0)).join('\n')

/** Words in a page's plain text, with the cheap-to-strip markdown syntax removed. */
export const wordCount = (text: string): number => {
  const plain = text
    .replace(/```/g, ' ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}(?:#{1,6}|>|[-*+]|\d+[.)]|- \[[ xX]\])\s+/gm, '')
    .replace(/^\s*(?:[-*_]\s*){3,}$/gm, ' ')
    .replace(/[*_`~]+/g, '')
  return plain.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length
}

export const wordsLabel = (text: string): string => {
  const n = wordCount(text)
  return `${n} ${n === 1 ? 'word' : 'words'}`
}
